import { DataSourceMode, Label, Panel, Textbox } from "@mcleod/components";
import { AutogenLayoutEquipmentGroupWarnings } from "./autogen/AutogenLayoutEquipmentGroupWarnings";
import { EquipmentGroupValidation, EquipmentGroupValidationResult } from "./EquipmentGroupValidation";
import { FieldUpdateEvent, ModelRow } from "@mcleod/core";

export class EquipmentGroupWarnings extends AutogenLayoutEquipmentGroupWarnings {
    private equipment: string[];

    async onLoad(): Promise<void> {
        // Instantiate a dummy row to display data
        this.mainDataSource.setRowsAndMode(DataSourceMode.ADD, [await this.mainDataSource.createBlankRow()]);
    }

    addFieldUpdateListener(modelRow: ModelRow) {
        if (modelRow["equipment_item_listener"] == null) {
            modelRow["equipment_item_listener"] = (event: FieldUpdateEvent) => {
                EquipmentGroupValidation.onEquipmentFieldUpdated(event, this);
            };
            modelRow.addAfterFieldUpdateListener(modelRow["equipment_item_listener"]);
        }
    }

    removeFieldUpdateListener(modelRow: ModelRow) {
        modelRow["_afterFieldUpdateListeners"] = modelRow["_afterFieldUpdateListeners"].filter((listener) => listener !== modelRow["equipment_item_listener"]);
    }

    display(validationResult: EquipmentGroupValidationResult) {
        if (validationResult == null) {
            validationResult = {
                errors: [], driverRegulatoryIssues: null, driverProhibitedValues: {},
                driverEvents: [], equipmentWarnings: [], maintenanceWarnings: [],
                enforcedDrivers: [], doublesCertificationWarnings: [], equipment: []
            };
        }
        this.equipment = validationResult.equipment;
        this.showRegulatoryRequirements(validationResult.driverRegulatoryIssues);
        this.showEquipmentWarnings(validationResult.equipmentWarnings);
        this.showProhibitedDriverErrors(validationResult.driverProhibitedValues);
        this.showMaintenanceWarnings(validationResult.maintenanceWarnings);
        this.showDoubleCertificationWarnings(validationResult.doublesCertificationWarnings);
        this.updateVisibility();
    }

    refresh(equipment: string[]): void {
        this.equipment = equipment;
        this.findAndRemoveNonUniqueComponents(this.panelWarningsRegulatory);
        this.findAndRemoveNonUniqueComponents(this.panelWarningsEquipment);
        this.findAndRemoveNonUniqueComponents(this.panelWarningsMaintenance);
        this.findAndRemoveNonUniqueComponents(this.panelWarningsDouble);
        this.updateVisibility();
    }

    updateVisibility() {
        this.panelRegulatoryRequirements.visible = this.panelWarningsRegulatory.components.length > 0;
        this.panelEquipmentIssuedWarnings.visible = this.panelWarningsEquipment.components.length > 0;
        this.panelMaintenanceWarnings.visible = this.panelWarningsMaintenance.components.length > 0;
        this.panelDoublesCertificationWarnings.visible = this.panelWarningsDouble.components.length > 0;
    }

    private isUnique(comp): boolean {
        const uniqueId = comp.id.split("-")[0]; // get the part before the first hyphen
        return this.equipment.includes(uniqueId);
    }

    private findAndRemoveNonUniqueComponents(panel: Panel): void {
       const filteredComps = panel.components.filter(this.isUnique.bind(this));
       panel.components = filteredComps;
    }

    static compExists(panel: Panel, identifier: string): boolean {
        return !!panel.findComponentById(identifier) || panel.components.some((comp) => comp.id.includes(identifier+"-"));
    }

    private hasRegulatoryRequirements(driverRegulatoryIssues) {
        for (const driver in driverRegulatoryIssues) {
            if (driverRegulatoryIssues[driver]?.warnings?.length > 0 || driverRegulatoryIssues[driver]?.errors?.length > 0) {
                return true;
            }
        }
        return false;
    }

    private showRegulatoryRequirements(driverRegulatoryIssues) {
        this.findAndRemoveNonUniqueComponents(this.panelWarningsRegulatory);
        const hasIssues = this.hasRegulatoryRequirements(driverRegulatoryIssues);
        if (hasIssues) {
            for (const driver_field in driverRegulatoryIssues) {
                const hasErrorsOrWarnings = driverRegulatoryIssues[driver_field].errors?.length > 0 || driverRegulatoryIssues[driver_field].warnings?.length > 0;
                if (hasErrorsOrWarnings) {
                    EquipmentGroupWarnings.populateRegulatoryIssuePanel(this.panelWarningsRegulatory, driverRegulatoryIssues[driver_field]);
                }
            }
        }
    }

    static populateRegulatoryIssuePanel(driverPanel: Panel, driver) {
        const warningStyleProps = { fontSize: "large", fontBold: true, marginBottom: 4 };
        driver.errors?.forEach((error, index) => {
            if (!EquipmentGroupWarnings.compExists(driverPanel, `${error.unique_id}-${index}-error`)) {
                driverPanel.add(new Label({
                    id: `${error.unique_id}-${index}-error-regulatory`,
                    text: `ALERT - ${error.message}`, color: "error", ...warningStyleProps
                }));
            }
        });
        driver.warnings?.forEach((warning, index) => {
            if (!EquipmentGroupWarnings.compExists(driverPanel, `${warning.unique_id}-${index}-warning`)) {
                driverPanel.add(new Label({
                    id: `${warning.unique_id}-${index}-warning-regulatory`,
                    text: `WARNING - ${warning.message}`, color: "warning", ...warningStyleProps
                }));
            }
        });
    }

    private showEquipmentWarnings(equipmentWarnings) {
        const warningStyleProps = { fontSize: "large", fontBold: true, marginBottom: 4 }
        this.findAndRemoveNonUniqueComponents(this.panelWarningsEquipment);
        if (equipmentWarnings.length > 0) {
            equipmentWarnings.forEach(warning => {
                if (!EquipmentGroupWarnings.compExists(this.panelWarningsEquipment, `${warning.unique_id}-${warning.id}`)) {
                    if (warning.equipment_requirement == "N") {
                        this.panelWarningsEquipment.add(new Label({
                            id: `${warning.unique_id}-${warning.id}-equipmentwarning`,
                            text: `RESTRICTION - ${warning.message}`, color: "error", ...warningStyleProps
                        }));
                    } else {
                        this.panelWarningsEquipment.add(new Label({
                            id: `${warning.unique_id}-${warning.id}-equipmentwarning`,
                            text: `WARNING - ${warning.message}`, color: "warning", ...warningStyleProps
                        }));
                    }
                }
            });
        }
    }

    private async showProhibitedDriverErrors(driverProhibitedValues) {
        this.panelProhibitedDrivers.visible = Object.entries(driverProhibitedValues).length > 0;
        this.activeRow.setValues(driverProhibitedValues);
        this.panelProhibitedDrivers.displayData(this.activeRow, null, 0);
        this.panelProhibitedDrivers.forEveryChildComponent(comp => {
            if (comp instanceof Textbox) {
                comp.visible = comp.text != "";
            }
        });
    }

    private showMaintenanceWarnings(maintenanceWarnings: any[]) {
        const warningStyleProps = { fontSize: "large", fontBold: true, marginBottom: 4 }
        this.findAndRemoveNonUniqueComponents(this.panelWarningsMaintenance);
        if (maintenanceWarnings.length > 0) {
            maintenanceWarnings.forEach(warning => {
                if (!EquipmentGroupWarnings.compExists(this.panelWarningsMaintenance, warning.unique_id)) {
                    this.panelWarningsMaintenance.add(new Label({
                        id: `${warning.unique_id}-maintenance`,
                        text: `WARNING - ${warning.message}`, color: "warning", ...warningStyleProps
                    }));
                }
            });
        }
    }

    private showDoubleCertificationWarnings(doubleCertificationWarnings: any[]) {
        const warningStyleProps = { fontSize: "large", fontBold: true, marginBottom: 4 }
        this.findAndRemoveNonUniqueComponents(this.panelWarningsDouble);
        if (doubleCertificationWarnings.length > 0) {
            doubleCertificationWarnings.forEach(warning => {
                if (!EquipmentGroupWarnings.compExists(this.panelWarningsDouble, warning.unique_id)) {
                    this.panelWarningsDouble.add(new Label({
                        id: `${warning.unique_id}-doubles`,
                        text: `RESTRICTION - ${warning.message}`, color: "error", ...warningStyleProps
                    }));
                }
            });
        } else {
            if (this.panelWarningsDouble.components.length > 0) {
                this.panelWarningsDouble.components = [];
            }
        }
    }
}
